// context/AuthContext.tsx
import { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { AuthService, UsersService } from '../api'; // ⬅️ UsersService kell a profiladatokhoz
import { OpenAPI } from '../api/core/OpenAPI';

type User = {
  id?: string;
  email: string;
  displayName?: string | null;
  profileImageUrl?: string | null;
};

type AuthContextType = {
  user: User | null;
  loading: boolean;
  register: (email: string, password: string) => Promise<void>;
  login: (email: string, password: string) => Promise<void>;
  logout: () => void;
  refreshMe: () => Promise<void>;        // ⬅️ új
  bust: number;                           // ⬅️ cache-buster a képre
};

const AuthContext = createContext<AuthContextType>({} as AuthContextType);

const API_BASE = OpenAPI.BASE || 'http://localhost:5051';
const joinUrl = (base: string, path: string) => base.replace(/\/+$/, '') + path;
const resolveAvatarBase = (url?: string | null) =>
  !url ? '/profile-placeholder.png' : url.startsWith('http') ? url : joinUrl(API_BASE, url);
const withBust = (url: string, seed: number) => (url.includes('?') ? `${url}&v=${seed}` : `${url}?v=${seed}`);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [bust, setBust] = useState<number>(Date.now());
  const navigate = useNavigate();

  const hydrateUserFromMe = async () => {
    // A UsersService.getApiUsersMe hozza a displayName + profileImageUrl mezőket
    const me = await UsersService.getApiUsersMe();
    setUser({
      id: me.id ?? undefined,
      email: me.email ?? '',
      displayName: me.displayName ?? null,
      profileImageUrl: me.profileImageUrl ?? null,
    });
    setBust(Date.now()); // ⬅️ minden frissítéskor új bust
  };

  const register = async (email: string, password: string) => {
    await AuthService.postApiAuthRegister({ email, password });
    await login(email, password);
  };

  const login = async (email: string, password: string) => {
    await AuthService.postApiAuthLogin({ email, password });
    await hydrateUserFromMe();
    navigate('/');
  };

  const logout = () => {
    setUser(null);
  };

  const refreshMe = async () => {
    await hydrateUserFromMe();
  };

  useEffect(() => {
    (async () => {
      try {
        await hydrateUserFromMe();
      } catch (err) {
        // nincs bejelentkezve, semmi gond
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  return (
    <AuthContext.Provider value={{ user, register, login, logout, loading, refreshMe, bust }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);

// segédfüggvények (ha máshol is kellene)
export const resolveAvatar = (url?: string | null, seed?: number) =>
  withBust(resolveAvatarBase(url), seed ?? Date.now());
